import { logger } from '@belong/logging';
import { Entry } from '@belong/types';
import { FAdobeTargetActivity } from './factories';
import {
  IAdobeTargetExperienceContent,
  IAdobeTargetActivityResponse,
  IFindAdobeTargetActivity,
  IGetExperienceSections,
  IUpdateSectionsWithAdobeTargetActivity,
  isIAdobeTargetActivityError
} from './types';
import { selfTerminate } from './selfTermination';
import { requestAdobeTargetActivity } from './requestTargetActivity';
import { CONFIG } from '../target-config';

export const API_ERROR = 'Error getting the Target Activity from Adobe';

// grab the mbox names from the activities and get the Target Activity from Adobe
// we're only dealing with one target at this time although can be used to make multiple changes on the page
export const findAdobeTargetActivity = async ({
  ctx,
  sessionId,
  supplementalDataId,
  unparsedPageEntry,
  isAuthenticatedCustomer,
  forceControlExperience
}: IFindAdobeTargetActivity): Promise<IAdobeTargetActivityResponse | undefined> => {
  const unparsedSections = unparsedPageEntry?.fields.sections || [];
  const terminationTimer = CONFIG.timeout || 200;

  // target sections will be an array of sections that have an mboxName
  const activitySections = findActivitySections(unparsedSections);

  const requestAdobeTargetActivityStart = Date.now();

  if (activitySections.length) {
    try {
      // check that there is an actual target
      const adobeTargetActivityResponse = await selfTerminate(
        requestAdobeTargetActivity({
          ctx,
          mboxLocation: activitySections[0].fields.mBoxName,
          sessionId,
          supplementalDataId,
          isAuthenticatedCustomer,
          forceControlExperience // this lets Adobe target know that the user accessing the page is a control/excluded group
        }),
        terminationTimer
      );

      if (adobeTargetActivityResponse) {
        const executionTime = Date.now() - requestAdobeTargetActivityStart;
        logger.info(`Adobe Target Activity request execution time :  ${executionTime}ms`);

        return (
          {
            ...adobeTargetActivityResponse,
            executionTime
          } || undefined
        );
      }
    } catch (e: any) {
      logger.error(API_ERROR, e);

      const executionTime = Date.now() - requestAdobeTargetActivityStart;
      logger.info(`Adobe Target Activity request execution time :  ${executionTime}ms`);

      return {
        errorMessage: e.message,
        errorDetail: e.stack,
        executionTime
      };
    }
  }

  // return undefined if there are no target sections
  return undefined;
};

// return the experience section from the activity that matches the returned value from Adobe
export const getExperienceSections = ({
  adobeTargetActivity,
  adobeTargetActivityResponse,
  forceControlExperience = false
}: IGetExperienceSections): Entry<any>[] => {
  // if a target activity hasn't been found return the control experience
  const showControlExperience = forceControlExperience || isIAdobeTargetActivityError(adobeTargetActivityResponse);

  if (showControlExperience) {
    return adobeTargetActivity.experiences[0].experienceContent;
  }

  const { mboxes } = adobeTargetActivityResponse.execute;
  const { options } = mboxes[0];

  // if there are no options then return the default experience
  // this is a fallback in case the Adobe Target activity is not active
  if (!options) {
    return adobeTargetActivity.experiences[0].experienceContent;
  }

  // find the experience in the activity that matches the returned value from Adobe
  const experienceSections =
    adobeTargetActivity.experiences.find(
      (experience: IAdobeTargetExperienceContent) => experience.experienceName === options[0].content.experienceName
    )?.experienceContent || adobeTargetActivity.experiences[0].experienceContent; // additional default fallback protection
  return experienceSections;
};

export const updateSectionsWithAdobeTargetActivity = ({
  adobeTargetActivityResponse,
  unparsedPageEntry,
  forceControlExperience = false
}: IUpdateSectionsWithAdobeTargetActivity): Entry<any> => {
  const unparsedSections = unparsedPageEntry?.fields.sections || [];
  const targetSections = findActivitySections(unparsedSections);

  // filter the unparsedSections for any target activities that don't have an mboxName. If they get through things will break
  const sanitisedUnparsedSections = unparsedSections.filter(
    (section: Entry<any>) =>
      section.sys.contentType.sys.id !== 'adobeTargetActivity' ||
      (section.sys.contentType.sys.id === 'adobeTargetActivity' && section.fields.mBoxName !== undefined)
  );

  const factoriedTargetSections = targetSections.map((section: Entry<any>) => FAdobeTargetActivity(section));

  if (targetSections) {
    let targetIndex = 0; // track target to use when we find a target section
    const parsedSections = sanitisedUnparsedSections.flatMap((section: Entry<any>) => {
      // flatMap here ensures that multiple sections are returned to parsedSections without being wrapped in an array
      if (section.sys.contentType.sys.id === 'adobeTargetActivity') {
        // match the mboxName from the section with the mboxName from the target
        const experienceSections = getExperienceSections({
          adobeTargetActivity: factoriedTargetSections[targetIndex],
          adobeTargetActivityResponse,
          forceControlExperience
        });
        targetIndex += 1;

        return experienceSections;
      }

      return section;
    });

    // return the page entry with the updated sections... or not
    return {
      ...(unparsedPageEntry as Entry<any>),
      fields: {
        ...(unparsedPageEntry?.fields ?? {}),
        sections: parsedSections
      }
    };
  }
  return unparsedPageEntry as Entry<any>;
};

export const findActivitySections = (unparsedSections: Entry<any>[]): Entry<any>[] => {
  return unparsedSections.filter(
    (section: Entry<any>) =>
      section.sys.contentType.sys.id === 'adobeTargetActivity' && section.fields.mBoxName !== undefined
  );
};
